import React, { useState, useEffect, useMemo, useCallback, useId, Fragment } from "react";
import Icon from "./icons/Icon";
import Modal from "./modals/Modal";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import { getAttendancePolicy } from "../services/attendancePolicyService";
import { approveOtRecord, rejectOtRecord } from "../services/otApprovalService";
import { getOtDisableReason } from "../utils/otApprovalPolicy";
import { loadActiveLocks, deriveLockStatus } from "../services/dtrLockDerivation";
import { formatFirestoreTimestamp } from "../utils/timeFormatting";
import { useToast } from "../contexts/ToastContext";
import "../styles/sections/ot-approval.css";
import { toYMDString } from "../utils/periodDate";
import { matchesEmployeeSearch } from "../utils/employeeSearch";

// Generate month options (last 6 months)
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
};

const MONTH_OPTIONS = getMonthOptions();

// 'YYYY-MM-DD' — the app-wide date standard (DESIGN-STANDARDS.md §13).
// toYMDString reads LOCAL Y/M/D components, never toISOString, so a Manila
// timestamp cannot render the previous day. Used by the DATE column and the
// reject modal, which must agree.
const formatDate = (dateStr) => toYMDString(dateStr) || "—";

// Every hours figure on this page renders to two decimals, so this page and Employee
// Time Records read identically — both display the same fields, and only the string
// differed (2.5 here vs 2.50 there). Applied to Hours Worked and Regular Hours too, so
// one panel cannot show `8 hrs` beside `2.50 hrs` — Threshold Applied included, for
// that consistency alone. (It is stored as an integer: AttendancePolicyPage parseInts
// the input before saving. The formatter is not there to tame a decimal it could hold.)
export const formatHours = (hours) => (Number(hours) || 0).toFixed(2);

// The OT-block size the record was actually rounded on, from the policyApplied
// snapshot. 0 and absent both read "Off" — a deliberate call, accepted as correct for
// a record with no snapshot, since the policy default is 0 anyway.
export const formatOtBlocks = (minutes) => (minutes ? `${minutes} min` : "Off");

// Format a Firestore timestamp or JS Date for the audit trail
const formatAuditTime = (val) => {
  if (!val) return "—";
  const date = val.toDate ? val.toDate() : new Date(val);
  return date.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.split(",")[0].split(" ");
  return parts.map((p) => p[0]).join("").toUpperCase().slice(0, 2);
};

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    pending: { background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)" },
    approved: { background: "var(--ui-success-tint)", color: "var(--ui-success-text)" },
    "auto-approved": { background: "transparent", color: "var(--ui-success-text)", border: "1px solid var(--ui-success)" },
    rejected: { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)" },
  };
  const labels = {
    pending: "Pending",
    approved: "Approved",
    "auto-approved": "Auto-Approved",
    rejected: "Rejected",
  };
  const s = styles[status] || styles.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "0.25rem 0.75rem", borderRadius: "20px",
      fontSize: "0.75rem", fontWeight: "600", whiteSpace: "nowrap",
      ...s,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: s.color,
      }} />
      {labels[status] || status}
    </span>
  );
};

// Audit trail action config
const AUDIT_STYLES = {
  submitted: { bg: "var(--ui-warning-tint)", color: "var(--ui-warning)", label: "Submitted for approval" },
  "auto-approved": { bg: "var(--ui-primary-tint)", color: "var(--ui-primary)", label: "Auto-approved" },
  approved: { bg: "var(--ui-success-tint)", color: "var(--ui-success)", label: "Approved" },
  rejected: { bg: "var(--ui-danger-tint)", color: "var(--ui-danger)", label: "Rejected" },
  reset: { bg: "var(--ui-surface-recessed)", color: "var(--ui-text-faint)", label: "Reset (DTR edited)" },
};

const OTApprovalPage = () => {
  const navigate = useNavigate();
  const { user, company, hasPermission } = useAuth();
  const canEditOT = hasPermission("time:ot:edit");
  const toast = useToast();

  // Data state
  const [loading, setLoading] = useState(true);
  const [otRecords, setOtRecords] = useState([]);
  const [userMap, setUserMap] = useState({});

  // Filter state
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[0]?.value || "");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Sort state
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // UI state
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Action state
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const rejectTitleId = useId();

  // Company approval policy ('self-approval' | 'maker-checker')
  const [approvalType, setApprovalType] = useState("self-approval");

  // Unique row identity across the aggregated multi-employee list. recordId
  // (rec_YYYYMMDD_###) is only unique within one employee's monthly DTR doc, so two
  // employees with a same-date record share a recordId — employeeId disambiguates.
  // NB: the real recordId (not this) is what's passed to the service + edit navigation.
  const rowKey = (r) => `${r.employeeId}_${r.recordId}`;

  // Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data when company or month changes
  const fetchData = useCallback(async () => {
    if (!company?.id || !selectedMonth) return;
    setLoading(true);

    try {
      // Fetch employees, users, DTRs, and the approval policy in parallel
      const [empSnap, userSnap, dtrSnap, policy, lockRuns] = await Promise.all([
        getDocs(query(collection(db, "companies", company.id, "employees"), where("role", "==", "employee"))),
        getDocs(collection(db, "companies", company.id, "users")),
        getDocs(query(collection(db, "companies", company.id, "dailyTimeRecords"), where("month", "==", selectedMonth))),
        getAttendancePolicy(company.id),
        loadActiveLocks(company.id), // derived DTR locks (C1); throws on read error → fail-closed below
      ]);
      // getDocs RESOLVES from the local cache when offline instead of rejecting
      // (proven at BL-081's /test-impl against real Firestore), so an offline load
      // would silently render "no OT records". Treat a cache-resolved DTR snapshot
      // as a failure → the catch below fails closed with the toast (BL-083).
      if (dtrSnap.metadata.fromCache) {
        throw new Error("dailyTimeRecords resolved from cache (offline?)");
      }
      setApprovalType(policy?.otApprovalType || "self-approval");

      // Build employee map: employeeId -> { name, department }
      const empMap = {};
      empSnap.docs.forEach((d) => {
        const data = d.data();
        const eid = data.employeeId;
        const name = data.personalInfo
          ? `${data.personalInfo.lastName || ""}, ${data.personalInfo.firstName || ""}`.trim()
          : "Unknown";
        empMap[eid] = { name, department: data.employment?.department || "" };
      });
      // Build user map: userDocId -> fullName
      const uMap = {};
      userSnap.docs.forEach((d) => {
        const data = d.data();
        uMap[d.id] = data.fullName || data.displayName || data.email || "Unknown User";
      });
      setUserMap(uMap);

      // Extract records with otApproval from DTR docs
      const records = [];
      dtrSnap.docs.forEach((docSnap) => {
        const docData = docSnap.data();
        const timeRecords = docData.timeRecords || [];
        timeRecords.forEach((rec) => {
          if (rec.otApproval) {
            const emp = empMap[rec.employeeId] || {};
            records.push({
              docId: docSnap.id,
              employeeId: rec.employeeId,
              employeeDocId: docData.employeeDocId,
              recordId: rec.id,
              date: rec.date,
              overtimeHours: rec.overtimeHours || 0,
              otApproval: rec.otApproval,
              createdBy: rec.createdBy,
              updatedBy: rec.updatedBy,
              timeIn: rec.timeIn,
              timeOut: rec.timeOut,
              hoursWorked: rec.hoursWorked || 0,
              regularHours: rec.regularHours || 0,
              nightDiffHours: rec.nightDiffHours || 0,
              isRestDay: rec.isRestDay || false,
              policyApplied: rec.policyApplied,
              lockStatus: deriveLockStatus(rec.employeeId, rec.date, lockRuns)?.status || null,
              employeeName: emp.name || "Unknown",
              department: emp.department || "",
            });
          }
        });
      });

      setOtRecords(records);
    } catch (err) {
      console.error("[OTApprovalPage] Error fetching data:", err);
      // Specific message (F6): the lock index is part of this fetch, so a failure
      // may be a lock-state read error — say approvals are paused, not just "failed".
      toast.error("Couldn't load OT approval data (incl. payroll lock state) — approvals paused; please retry.");
      setOtRecords([]); // fail-closed (C10a): a load error (incl. the lock index) clears actionable rows
    } finally {
      setLoading(false);
    }
  }, [company?.id, selectedMonth, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Departments list for filter
  const departments = useMemo(() => {
    const depts = new Set(otRecords.map((r) => r.department).filter(Boolean));
    return [...depts].sort();
  }, [otRecords]);

  // Filtered + sorted records
  const filteredRecords = useMemo(() => {
    let result = otRecords;
    if (statusFilter) result = result.filter((r) => r.otApproval.status === statusFilter);
    if (departmentFilter) result = result.filter((r) => r.department === departmentFilter);
    if (searchTerm) {
      // The row carries employeeName ("Last, First") and employeeId.
      result = result.filter((r) => matchesEmployeeSearch(r, searchTerm));
    }
    const sorted = [...result].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "employee": aVal = a.employeeName; bVal = b.employeeName; break;
        case "date": aVal = a.date; bVal = b.date; break;
        case "otHours": aVal = a.overtimeHours; bVal = b.overtimeHours; break;
        case "status": aVal = a.otApproval.status; bVal = b.otApproval.status; break;
        default: aVal = a.date; bVal = b.date;
      }
      // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
      // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
      // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
      const c = typeof aVal === "string" && typeof bVal === "string"
        ? aVal.localeCompare(bVal)
        : aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === "asc" ? c : -c;
    });
    return sorted;
  }, [otRecords, statusFilter, departmentFilter, searchTerm, sortField, sortDirection]);

  // Stats
  const stats = useMemo(() => ({
    total: filteredRecords.length,
    pending: filteredRecords.filter((r) => r.otApproval.status === "pending").length,
    approved: filteredRecords.filter((r) => r.otApproval.status === "approved").length,
    autoApproved: filteredRecords.filter((r) => r.otApproval.status === "auto-approved").length,
    rejected: filteredRecords.filter((r) => r.otApproval.status === "rejected").length,
  }), [filteredRecords]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  // Maker-checker / payroll-lock disabling (policy-aware). The transaction in
  // otApprovalService re-checks this authoritatively — this is the UI layer.
  const getDisableReason = (record) =>
    getOtDisableReason({ record, currentUserId: user?.userData?.userDocId, approvalType, lockStatus: record.lockStatus });

  // Approve handler — delegates to the transactional service (server-authoritative).
  const handleApprove = async (record) => {
    const key = rowKey(record);
    setActionLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const { otApproval } = await approveOtRecord({
        companyId: company.id,
        employeeId: record.employeeId,
        recordId: record.recordId, // real recordId — service derives the DTR doc id from it
        approverUserId: user.userData.userDocId,
      });
      // Optimistic local update. NB: do NOT set updatedBy — the approver is not
      // the maker (the service leaves updatedBy untouched on purpose).
      setOtRecords((prev) =>
        prev.map((r) => (rowKey(r) === key ? { ...r, otApproval } : r))
      );
      toast.success("OT record approved");
    } catch (err) {
      console.error("[OTApprovalPage] Approve error:", err);
      toast.error(err.message || "Failed to approve OT record");
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Reject handler — delegates to the transactional service.
  const handleReject = async () => {
    if (!rejectReason.trim() || !rejectModal) return;
    const record = rejectModal;
    const key = rowKey(record);
    setActionLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const { otApproval } = await rejectOtRecord({
        companyId: company.id,
        employeeId: record.employeeId,
        recordId: record.recordId, // real recordId — service derives the DTR doc id from it
        approverUserId: user.userData.userDocId,
        reason: rejectReason.trim(),
      });
      setOtRecords((prev) =>
        prev.map((r) => (rowKey(r) === key ? { ...r, otApproval } : r))
      );
      toast.success("OT record rejected");
      setRejectModal(null);
      setRejectReason("");
    } catch (err) {
      console.error("[OTApprovalPage] Reject error:", err);
      toast.error(err.message || "Failed to reject OT record");
    } finally {
      setActionLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Navigate to employee time records with edit modal pre-opened
  const navigateToEditDTR = (record) => {
    navigate(
      `/company/${company.slug}/time-tracking/employee/${record.employeeId}?editRecord=${record.recordId}`
    );
  };

  // Button styling mirrors PayrollApprovalPage (inline, not CSS classes).
  const actionBtnStyle = (kind, disabled, isLoading) => ({
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
    border: `1px solid ${kind === "approve" ? "var(--ui-success)" : kind === "reject" ? "var(--ui-danger)" : "var(--ui-primary)"}`,
    background: disabled ? "var(--ui-surface-recessed)" : kind === "approve" ? "var(--ui-success)" : "var(--ui-surface)",
    color: disabled ? "var(--ui-text-faint)" : kind === "approve" ? "var(--ui-surface)" : kind === "reject" ? "var(--ui-danger)" : "var(--ui-primary)",
    opacity: isLoading ? 0.6 : 1,
  });

  // Render action buttons for a record
  const renderActions = (record) => {
    if (!canEditOT) return null;
    const disableReason = getDisableReason(record);
    const isLoading = actionLoading[rowKey(record)];
    const disabled = !!disableReason || isLoading;
    const status = record.otApproval.status;

    if (status === "pending") {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleApprove(record); }}
            disabled={disabled}
            title={disableReason || ""}
            style={actionBtnStyle("approve", disabled, isLoading)}
          >Approve</button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setRejectModal(record); }}
            disabled={disabled}
            title={disableReason || ""}
            style={actionBtnStyle("reject", disabled, isLoading)}
          >Reject</button>
        </div>
      );
    }

    if (status === "approved" || status === "auto-approved") {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setRejectModal(record); }}
          disabled={disabled}
          title={disableReason || ""}
          style={actionBtnStyle("reject", disabled, isLoading)}
        >Reject</button>
      );
    }

    if (status === "rejected") {
      return (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleApprove(record); }}
            disabled={disabled}
            title={disableReason || ""}
            style={actionBtnStyle("approve", disabled, isLoading)}
          >Approve</button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigateToEditDTR(record); }}
            style={actionBtnStyle("edit", false, false)}
          >Edit DTR</button>
        </div>
      );
    }

    return null;
  };

  // Render expanded detail panel
  const renderDetailPanel = (record) => (
    <tr style={{ background: "white", borderBottom: "1px solid var(--ui-surface-recessed)" }}>
      <td colSpan="6" style={{ padding: 0 }}>
        <div style={{ borderTop: "2px solid var(--ui-primary)", display: "flex", gap: 0 }}>
          {/* Left: DTR Details */}
          <div style={{ flex: 1, padding: "20px 24px", borderRight: "1px solid var(--ui-surface-recessed)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
              DTR Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Time In</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatFirestoreTimestamp(record.timeIn) || "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Time Out</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatFirestoreTimestamp(record.timeOut) || "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Hours Worked</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatHours(record.hoursWorked)} hrs
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Regular Hours</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatHours(record.regularHours)} hrs
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>OT Hours</div>
                <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "var(--ui-primary)" }}>
                  {formatHours(record.overtimeHours)} hrs
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Day Type</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {record.isRestDay ? "Rest Day" : "Regular Day"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>Threshold Applied</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatHours(record.otApproval.otAutoApprovalThreshold)} hrs
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginBottom: 2 }}>OT Blocks</div>
                <div style={{ fontSize: 13, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatOtBlocks(record.policyApplied?.otIncrement)}
                </div>
              </div>
            </div>
            {/* Actions in detail panel */}
            {canEditOT && (
              <div style={{ display: "flex", gap: 10, paddingTop: 16, marginTop: 16, borderTop: "1px solid var(--ui-surface-recessed)" }}>
                {renderActions(record)}
              </div>
            )}
          </div>

          {/* Right: Audit Trail */}
          <div style={{ flex: 1, padding: "20px 24px" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>
              Audit Trail
            </div>
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              {(record.otApproval.auditTrail || []).map((entry, idx, arr) => {
                const style = AUDIT_STYLES[entry.action] || AUDIT_STYLES.submitted;
                return (
                  <div key={idx} style={{ display: "flex", gap: 12, padding: "8px 0", position: "relative" }}>
                    {/* Connector line */}
                    {idx < arr.length - 1 && (
                      <div style={{
                        position: "absolute", left: 9, top: 28, bottom: -8,
                        width: 2, background: "var(--ui-border)",
                      }} />
                    )}
                    {/* Dot */}
                    <div style={{
                      width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                      background: style.bg, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: style.color }} />
                    </div>
                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ui-text)" }}>{style.label}</div>
                      <div style={{ fontSize: 11, color: "var(--ui-text-faint)", marginTop: 2 }}>
                        {userMap[entry.by] || entry.by || "Unknown"} · {formatAuditTime(entry.at)}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ui-text-muted)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                        OT: {formatHours(entry.otHours)} hrs
                      </div>
                      {entry.reason && (
                        <div style={{
                          fontSize: 12, color: "var(--ui-danger-text)", marginTop: 4,
                          padding: "6px 10px", background: "var(--ui-danger-tint)", borderRadius: 6, fontStyle: "italic",
                        }}>
                          "{entry.reason}"
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );

  // Loading state
  if (loading) {
    return (
      <div style={{ fontFamily: "var(--font-family-primary)" }}>
        <h1 style={{ fontSize: isMobile ? "1.125rem" : "1.25rem", fontWeight: 700, color: "var(--ui-primary)", margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0" }}>
          OT Approval
        </h1>
        <div style={{ background: "white", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "60px 24px", textAlign: "center", color: "var(--ui-text-muted)" }}>
          Loading OT approval data...
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      {/* Page Title + Policy Badge */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "0.75rem", margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
      }}>
        <h1 style={{ fontSize: isMobile ? "1.125rem" : "1.25rem", fontWeight: 700, color: "var(--ui-primary)", margin: 0 }}>
          OT Approval
        </h1>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
          background: approvalType === "maker-checker" ? "var(--ui-primary-tint)" : "var(--ui-surface-recessed)",
          color: approvalType === "maker-checker" ? "var(--ui-primary)" : "var(--ui-text-secondary)",
          border: `1px solid ${approvalType === "maker-checker" ? "var(--ui-primary)" : "var(--ui-border)"}`,
        }}>
          Policy: {approvalType === "maker-checker" ? "Maker-checker" : "Self-approval"}
        </span>
      </div>

      {/* Mobile Stats Cards */}
      {isMobile && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Pending", value: stats.pending, color: "var(--ui-warning)" },
            { label: "Approved", value: stats.approved + stats.autoApproved, color: "var(--ui-primary)" },
            { label: "Rejected", value: stats.rejected, color: "var(--ui-danger)" },
            { label: "Total", value: stats.total, color: "var(--ui-primary)" },
          ].map((s) => (
            <div key={s.label} style={{
              background: "white", borderRadius: 8, border: "1px solid var(--ui-border)", padding: "12px 16px",
              borderLeft: `4px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: s.color, fontVariantNumeric: "tabular-nums" }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Controls Bar */}
      {!isMobile ? (
        <div style={{
          display: "flex", alignItems: "center", gap: "0.75rem",
          padding: "0.625rem 1rem", background: "white", borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: "0.75rem",
        }}>
          <input
            type="text"
            placeholder="Search by employee name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1, minWidth: 160, padding: "0.5rem 0.75rem",
              border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem",
              fontFamily: "inherit",
            }}
          />
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              minWidth: 140, padding: "0.5rem 0.75rem",
              border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem",
              fontFamily: "inherit", background: "white",
            }}
          >
            {MONTH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              minWidth: 120, padding: "0.5rem 0.75rem",
              border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem",
              fontFamily: "inherit", background: "white",
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="auto-approved">Auto-Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {departments.length > 0 && (
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              style={{
                minWidth: 140, padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem",
                fontFamily: "inherit", background: "white",
              }}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          )}
        </div>
      ) : (
        <div style={{
          background: "white", padding: "1rem", borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: "0.75rem",
        }}>
          <input
            type="text"
            placeholder="Search by employee name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "100%", marginBottom: "0.5rem", padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem", fontFamily: "inherit" }}
          />
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ width: "100%", marginBottom: "0.5rem", padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem", fontFamily: "inherit" }}>
            {MONTH_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: "100%", marginBottom: "0.5rem", padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 6, fontSize: "0.8125rem", fontFamily: "inherit" }}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="auto-approved">Auto-Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      )}

      {/* Table */}
      <div style={{ background: "white", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--ui-gradient-primary)" }}>
              {[
                { key: "employee", label: "Employee", align: "left" },
                { key: "date", label: "Date", align: "left" },
                { key: "otHours", label: "OT Hours", align: "center" },
                { key: "status", label: "Status", align: "center" },
                { key: null, label: "Entered By", align: "left" },
                { key: null, label: "Actions", align: "center" },
              ].map((col, i) => (
                <th
                  key={i}
                  onClick={col.key ? () => handleSort(col.key) : undefined}
                  style={{
                    padding: "14px 16px", textAlign: col.align, fontWeight: 600,
                    fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px",
                    color: "white", cursor: col.key ? "pointer" : "default",
                    userSelect: "none", whiteSpace: "nowrap",
                  }}
                >
                  {col.label}{col.key ? sortIndicator(col.key) : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody key={`${statusFilter}-${departmentFilter}-${searchTerm}`}>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: "60px 24px", textAlign: "center", color: "var(--ui-text-muted)", fontSize: 14 }}>
                  {otRecords.length === 0
                    ? "No overtime records with approval status found for this month."
                    : "No records match your filters."}
                </td>
              </tr>
            ) : (
              filteredRecords.map((record) => (
                <Fragment key={rowKey(record)}>
                  <tr
                    className={`ot-row${expandedRowId === rowKey(record) ? " expanded" : ""}`}
                    onClick={() => setExpandedRowId(expandedRowId === rowKey(record) ? null : rowKey(record))}
                  >
                    {/* Employee */}
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%",
                          background: "linear-gradient(135deg, var(--ui-primary-tint) 0%, var(--ui-primary-tint) 100%)",
                          color: "var(--ui-primary)", display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: 13, flexShrink: 0,
                        }}>
                          {getInitials(record.employeeName)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, color: "var(--ui-text)", fontSize: "0.875rem" }}>{record.employeeName}</div>
                          <div style={{ fontSize: 11, color: "var(--ui-text-faint)", fontVariantNumeric: "tabular-nums" }}>{record.employeeId}</div>
                        </div>
                      </div>
                    </td>
                    {/* Date */}
                    <td style={{ padding: "12px 16px", fontSize: "0.875rem", fontWeight: 500, color: "var(--ui-text)", whiteSpace: "nowrap" }}>
                      {formatDate(record.date)}
                    </td>
                    {/* OT Hours */}
                    <td style={{
                      padding: "12px 16px", textAlign: "center", fontSize: "0.875rem",
                      fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ui-text)",
                      textDecoration: record.otApproval.status === "rejected" ? "line-through" : "none",
                      opacity: record.otApproval.status === "rejected" ? 0.5 : 1,
                    }}>
                      {formatHours(record.overtimeHours)} hrs
                    </td>
                    {/* Status */}
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      <StatusBadge status={record.otApproval.status} />
                    </td>
                    {/* Entered By */}
                    <td style={{ padding: "12px 16px", fontSize: "0.8125rem", color: "var(--ui-text-muted)" }}>
                      {userMap[record.updatedBy ?? record.createdBy] || "Unknown"}
                    </td>
                    {/* Actions */}
                    <td style={{ padding: "12px 16px", textAlign: "center" }}>
                      {renderActions(record)}
                    </td>
                  </tr>
                  {expandedRowId === rowKey(record) && renderDetailPanel(record)}
                </Fragment>
              ))
            )}
          </tbody>
        </table>

        {/* Footer */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px",
          background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
          borderTop: "1px solid var(--ui-border)",
        }}>
          <div style={{ display: "flex", gap: 28 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Total Records</div>
              <div style={{ fontSize: 20, fontWeight: 600, color: "var(--ui-primary)", fontVariantNumeric: "tabular-nums" }}>{stats.total}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Pending</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-warning)", fontVariantNumeric: "tabular-nums" }}>{stats.pending}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Approved</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-primary-hover)", fontVariantNumeric: "tabular-nums" }}>{stats.approved}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Auto-Approved</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-primary-hover)", fontVariantNumeric: "tabular-nums" }}>{stats.autoApproved}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>Rejected</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-danger)", fontVariantNumeric: "tabular-nums" }}>{stats.rejected}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, fontStyle: "italic", color: "var(--ui-text-muted)" }}>
            Click on a row to view details
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <Modal
          isOpen
          onClose={() => { setRejectModal(null); setRejectReason(""); }}
          busy={!!actionLoading[rowKey(rejectModal)]}
          labelledBy={rejectTitleId}
          style={{
            background: "white", borderRadius: 14, width: 480, maxWidth: "100%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden",
          }}
        >
            {/* Modal Header */}
            <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--ui-surface-recessed)", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%", display: "flex",
                alignItems: "center", justifyContent: "center",
                background: "var(--ui-danger-tint)", color: "var(--ui-danger)",
              }}>
                <Icon name="x-circle" size={18} />
              </div>
              <div id={rejectTitleId} style={{ fontSize: 16, fontWeight: 600, color: "var(--ui-text)" }}>Reject Overtime</div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              {/* Context */}
              <div style={{ display: "flex", gap: 16, padding: "12px 14px", background: "var(--ui-bg)", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--ui-text-faint)", fontWeight: 500 }}>Employee</div>
                  <div style={{ fontWeight: 500, color: "var(--ui-text)" }}>{rejectModal.employeeName} ({rejectModal.employeeId})</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--ui-text-faint)", fontWeight: 500 }}>Date</div>
                  <div style={{ fontWeight: 500, color: "var(--ui-text)" }}>{formatDate(rejectModal.date)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--ui-text-faint)", fontWeight: 500 }}>OT Hours</div>
                  <div style={{ fontWeight: 500, color: "var(--ui-primary)", fontVariantNumeric: "tabular-nums" }}>{formatHours(rejectModal.overtimeHours)} hrs</div>
                </div>
              </div>

              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ui-text-secondary)", marginBottom: 6 }}>
                Reason for rejection <span style={{ color: "var(--ui-danger)" }}>*</span>
              </div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this overtime is being rejected..."
                style={{
                  width: "100%", minHeight: 80, padding: "10px 14px",
                  border: "1px solid var(--ui-border)", borderRadius: 8, fontSize: 13,
                  fontFamily: "inherit", color: "var(--ui-text)", resize: "vertical",
                }}
              />
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "16px 24px", borderTop: "1px solid var(--ui-surface-recessed)", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => { setRejectModal(null); setRejectReason(""); }}
                disabled={!!actionLoading[rowKey(rejectModal)]}
                style={{
                  padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                  fontFamily: "inherit", cursor: "pointer", background: "none",
                  color: "var(--ui-text-secondary)", border: "1px solid var(--ui-border)",
                }}
              >Cancel</button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading[rowKey(rejectModal)]}
                style={{
                  padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500,
                  fontFamily: "inherit", cursor: !rejectReason.trim() ? "not-allowed" : "pointer",
                  background: !rejectReason.trim() ? "var(--ui-border)" : "var(--ui-danger)",
                  color: !rejectReason.trim() ? "var(--ui-text-faint)" : "white", border: "none",
                }}
              >Reject OT</button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default OTApprovalPage;
